import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Trash2,
  ChevronDown,
  ChevronUp,
  Shirt,
  Package,
  Send as SendIcon,
  FileText,
  X,
  Plus,
  Loader2,
} from "lucide-react";
import { API } from "../../lib/api";

const STATUS_OPTIONS = [
  { key: "new", label: "Nieuw" },
  { key: "in_progress", label: "In behandeling" },
  { key: "quoted", label: "Offerte verzonden" },
  { key: "won", label: "Gewonnen" },
  { key: "lost", label: "Verloren" },
  { key: "archived", label: "Archief" },
];

const STATUS_COLOR = {
  new: "#ff1e1e",
  in_progress: "#f59e0b",
  quoted: "#3b82f6",
  won: "#10b981",
  lost: "#6b7280",
  archived: "#4b5563",
};

const AdminRequests = () => {
  const { authHeaders } = useOutletContext();
  const navigate = useNavigate();
  const [tab, setTab] = useState("consumer"); // consumer | b2b | merch
  const [consumer, setConsumer] = useState([]);
  const [b2b, setB2b] = useState([]);
  const [merch, setMerch] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceReq, setInvoiceReq] = useState(null); // b2b request being converted to invoice
  const [orderReq, setOrderReq] = useState(null); // consumer request being converted to order

  const load = async () => {
    setLoading(true);
    try {
      const [c, b, m, a] = await Promise.all([
        axios.get(`${API}/admin/requests/consumer`, { headers: authHeaders }),
        axios.get(`${API}/admin/requests/b2b`, { headers: authHeaders }),
        axios.get(`${API}/admin/requests/merch-interest`, { headers: authHeaders }),
        axios.get(`${API}/admin/requests/analytics`, { headers: authHeaders }),
      ]);
      setConsumer(c.data || []);
      setB2b(b.data || []);
      setMerch(m.data || []);
      setAnalytics(a.data || null);
    } catch (e) {
      toast.error("Aanvragen laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (kind, id, status, notes) => {
    try {
      await axios.patch(
        `${API}/admin/requests/${kind}/${id}`,
        { status, notes: notes ?? undefined },
        { headers: authHeaders }
      );
      toast.success("Status bijgewerkt");
      load();
    } catch (e) {
      toast.error("Bijwerken mislukt");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Aanvraag verwijderen? Dit kan niet ongedaan gemaakt worden.")) return;
    try {
      await axios.delete(`${API}/admin/requests/${kind}/${id}`, { headers: authHeaders });
      toast.success("Verwijderd");
      load();
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div data-testid="admin-requests-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-4xl">AANVRAGEN</h2>
        <button
          data-testid="requests-refresh"
          onClick={load}
          className="btn-ghost-red px-4 py-2 text-xs font-mono-x tracking-widest"
        >
          VERNIEUWEN
        </button>
      </div>

      {/* Analytics funnel */}
      {analytics && <AnalyticsFunnel analytics={analytics} />}

      {/* Tabs */}
      <div className="border-b border-[var(--tha-border)] flex mb-6">
        <TabBtn
          active={tab === "consumer"}
          onClick={() => setTab("consumer")}
          testId="tab-consumer"
          icon={User}
          label="CONSUMENTEN"
          count={consumer.length}
        />
        <TabBtn
          active={tab === "b2b"}
          onClick={() => setTab("b2b")}
          testId="tab-b2b"
          icon={Building2}
          label="ZAKELIJK"
          count={b2b.length}
        />
        <TabBtn
          active={tab === "merch"}
          onClick={() => setTab("merch")}
          testId="tab-merch"
          icon={Shirt}
          label="MERCH INTERESSE"
          count={merch.length}
        />
      </div>

      {loading ? (
        <div className="p-10 text-center text-[var(--tha-muted)] font-mono-x">
          Laden…
        </div>
      ) : tab === "consumer" ? (
        <RequestList
          kind="consumer"
          list={consumer}
          onStatus={updateStatus}
          onDelete={remove}
          onOrder={setOrderReq}
        />
      ) : tab === "b2b" ? (
        <RequestList
          kind="b2b"
          list={b2b}
          onStatus={updateStatus}
          onDelete={remove}
          onInvoice={setInvoiceReq}
        />
      ) : (
        <MerchList list={merch} onDelete={(id) => remove("merch-interest", id)} />
      )}

      {invoiceReq && (
        <RequestToInvoiceModal
          req={invoiceReq}
          authHeaders={authHeaders}
          onClose={() => setInvoiceReq(null)}
          onSuccess={(invoice) => {
            setInvoiceReq(null);
            toast.success(`Factuur ${invoice.invoice_number} aangemaakt`);
            load();
            navigate("/admin/b2b-invoices");
          }}
        />
      )}

      {orderReq && (
        <ConsumerToOrderModal
          req={orderReq}
          authHeaders={authHeaders}
          onClose={() => setOrderReq(null)}
          onSuccess={(res) => {
            setOrderReq(null);
            toast.success(`Bestelling aangemaakt — € ${res.total.toFixed(2)}`);
            load();
            navigate("/admin/orders");
          }}
        />
      )}
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`relative px-4 sm:px-6 py-4 inline-flex items-center gap-3 transition-colors ${
      active ? "text-white" : "text-[var(--tha-muted)] hover:text-white"
    }`}
  >
    <Icon size={14} />
    <span className="font-display text-sm sm:text-base tracking-widest">{label}</span>
    <span
      className={`text-[10px] font-mono-x tracking-widest px-1.5 py-0.5 border ${
        active
          ? "border-[var(--tha-red)] text-[var(--tha-red)]"
          : "border-[var(--tha-border)] text-[var(--tha-muted)]"
      }`}
    >
      {count}
    </span>
    {active && (
      <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--tha-red)]" />
    )}
  </button>
);

const RequestList = ({ kind, list, onStatus, onDelete, onInvoice, onOrder }) => {
  if (list.length === 0) {
    return (
      <div className="p-10 text-center text-[var(--tha-muted)] font-mono-x">
        Geen aanvragen gevonden.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {list.map((r) => (
        <RequestRow
          key={r.id}
          kind={kind}
          req={r}
          onStatus={onStatus}
          onDelete={onDelete}
          onInvoice={onInvoice}
          onOrder={onOrder}
        />
      ))}
    </div>
  );
};

const RequestRow = ({ kind, req, onStatus, onDelete, onInvoice, onOrder }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(req.notes || "");
  const isB2B = kind === "b2b";
  const title = isB2B ? req.company_name : req.name;
  const subtitle = isB2B
    ? `${req.contact_name}${req.business_type ? ` · ${req.business_type}` : ""}`
    : `${req.postal_code || ""} ${req.city || ""}`;

  const created = req.created_at
    ? new Date(req.created_at).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  const statusColor = STATUS_COLOR[req.status] || "#6b7280";

  return (
    <div
      data-testid={`request-row-${req.id}`}
      className="border border-[var(--tha-border)] bg-[#0a0a0a]"
    >
      <div
        className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`request-row-header-${req.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isB2B ? (
            <Building2 size={16} className="text-[var(--tha-red)] shrink-0" />
          ) : (
            <User size={16} className="text-[var(--tha-red)] shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-display text-lg truncate">{title}</div>
            <div className="text-xs font-mono-x text-[var(--tha-muted)] truncate">
              {subtitle} · {created}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-[10px] font-mono-x tracking-widest px-2 py-1 border"
            style={{ color: statusColor, borderColor: statusColor }}
          >
            {STATUS_OPTIONS.find((s) => s.key === req.status)?.label || req.status}
          </span>
          <button
            data-testid={`request-toggle-${req.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            className="p-2 hover:bg-white/5"
            aria-label="Details"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            data-testid={`request-delete-${req.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(kind, req.id);
            }}
            className="p-2 hover:bg-white/5 text-[var(--tha-muted)] hover:text-[var(--tha-red)]"
            aria-label="Verwijder"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[var(--tha-border)] p-5 bg-[#050505]">
          {/* Contact grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <InfoLine icon={Mail} label="E-mail">
              <a href={`mailto:${req.email}`} className="text-[var(--tha-red)] hover:underline">
                {req.email}
              </a>
            </InfoLine>
            <InfoLine icon={Phone} label="Telefoon">
              <a href={`tel:${req.phone}`} className="text-white hover:underline">
                {req.phone}
              </a>
            </InfoLine>
            {isB2B && req.kvk && (
              <InfoLine label="KvK" value={req.kvk} />
            )}
            {isB2B && req.vat_number && (
              <InfoLine label="BTW-nummer" value={req.vat_number} />
            )}
            {isB2B && (req.address || req.city) && (
              <InfoLine icon={MapPin} label="Adres" value={`${req.address || ""} ${req.city || ""}`} />
            )}
            {!isB2B && req.city && (
              <InfoLine icon={MapPin} label="Plaats" value={req.city} />
            )}
            {isB2B && req.estimated_volume && (
              <InfoLine label="Geschat volume" value={req.estimated_volume} />
            )}
          </div>

          {/* Items / interests */}
          {!isB2B && (req.items || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                GEWENSTE PRODUCTEN
              </div>
              <ul className="text-sm text-white space-y-1">
                {(req.items || []).map((it, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Package size={12} className="text-[var(--tha-red)]" />
                    <span>
                      {it.quantity}× {it.product_name}
                      {it.variant_label ? ` — ${it.variant_label}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isB2B && (req.interested_products || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                INTERESSE IN
              </div>
              <div className="flex flex-wrap gap-2">
                {(req.interested_products || []).map((p, i) => (
                  <span
                    key={i}
                    className="text-xs font-mono-x px-2 py-1 border border-[var(--tha-border)] text-white"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {req.message && (
            <div className="mt-4">
              <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                BERICHT
              </div>
              <div className="text-sm text-white whitespace-pre-wrap border-l-2 border-[var(--tha-red)] pl-4 py-2 bg-[#0a0a0a]">
                {req.message}
              </div>
            </div>
          )}

          {/* Status workflow */}
          <div className="mt-6 pt-4 border-t border-[var(--tha-border)]">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
              STATUS
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`request-status-${req.id}-${s.key}`}
                  onClick={() => onStatus(kind, req.id, s.key, notes)}
                  className={`text-xs font-mono-x tracking-widest px-3 py-2 border transition-colors ${
                    req.status === s.key
                      ? "border-[var(--tha-red)] bg-[#1a0303] text-white"
                      : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-white hover:border-white"
                  }`}
                >
                  {s.label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
              INTERNE NOTITIES
            </div>
            <textarea
              data-testid={`request-notes-${req.id}`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Bijv. contactmoment, prijs afgesproken..."
              className="w-full px-3 py-2 bg-[#0a0a0a] border border-[var(--tha-border)] text-sm text-white resize-none focus:border-white outline-none"
            />
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <a
                href={`mailto:${req.email}`}
                className="text-xs font-mono-x tracking-widest px-3 py-2 border border-[var(--tha-border)] hover:border-white text-white inline-flex items-center gap-2"
              >
                <SendIcon size={12} /> MAIL {isB2B ? req.contact_name?.split(" ")[0]?.toUpperCase() : req.name?.split(" ")[0]?.toUpperCase()}
              </a>
              {isB2B && !req.linked_invoice_id && (
                <button
                  data-testid={`request-to-invoice-${req.id}`}
                  onClick={() => onInvoice?.(req)}
                  className="text-xs font-mono-x tracking-widest px-3 py-2 border border-[var(--tha-red)] text-[var(--tha-red)] hover:bg-[#1a0303] inline-flex items-center gap-2"
                >
                  <FileText size={12} /> NAAR FACTUUR
                </button>
              )}
              {isB2B && req.linked_invoice_id && (
                <span className="text-xs font-mono-x tracking-widest px-3 py-2 border border-[#10b981] text-[#10b981] inline-flex items-center gap-2">
                  <FileText size={12} /> FACTUUR {req.linked_invoice_number}
                </span>
              )}
              {!isB2B && !req.linked_order_id && (
                <button
                  data-testid={`request-to-order-${req.id}`}
                  onClick={() => onOrder?.(req)}
                  className="text-xs font-mono-x tracking-widest px-3 py-2 border border-[var(--tha-red)] text-[var(--tha-red)] hover:bg-[#1a0303] inline-flex items-center gap-2"
                >
                  <Package size={12} /> MAAK BESTELLING
                </button>
              )}
              {!isB2B && req.linked_order_id && (
                <span className="text-xs font-mono-x tracking-widest px-3 py-2 border border-[#10b981] text-[#10b981] inline-flex items-center gap-2">
                  <Package size={12} /> BESTELLING AANGEMAAKT
                </span>
              )}
              <button
                data-testid={`request-save-notes-${req.id}`}
                onClick={() => onStatus(kind, req.id, req.status, notes)}
                className="text-xs font-mono-x tracking-widest px-3 py-2 btn-red"
              >
                NOTITIES OPSLAAN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoLine = ({ icon: Icon, label, value, children }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon size={12} className="mt-1 text-[var(--tha-muted)] shrink-0" />}
    <div className="min-w-0">
      <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">{label}</div>
      <div className="text-white truncate">{children || value}</div>
    </div>
  </div>
);

const MerchList = ({ list, onDelete }) => {
  if (list.length === 0) {
    return (
      <div className="p-10 text-center text-[var(--tha-muted)] font-mono-x">
        Nog geen merch-aanmeldingen.
      </div>
    );
  }
  return (
    <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--tha-border)] text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
            <th className="text-left p-3">E-MAIL</th>
            <th className="text-left p-3">NAAM</th>
            <th className="text-left p-3">INTERESSES</th>
            <th className="text-left p-3">DATUM</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((m) => (
            <tr
              key={m.id}
              data-testid={`merch-row-${m.id}`}
              className="border-b border-[var(--tha-border)] last:border-0 hover:bg-white/5"
            >
              <td className="p-3 text-white">
                <a href={`mailto:${m.email}`} className="hover:text-[var(--tha-red)]">
                  {m.email}
                </a>
              </td>
              <td className="p-3 text-white">{m.name || "—"}</td>
              <td className="p-3 text-[var(--tha-muted)] text-xs font-mono-x">
                {(m.interests || []).join(", ") || "—"}
              </td>
              <td className="p-3 text-[var(--tha-muted)] text-xs font-mono-x">
                {m.created_at ? new Date(m.created_at).toLocaleDateString("nl-NL") : "—"}
              </td>
              <td className="p-3 text-right">
                <button
                  data-testid={`merch-delete-${m.id}`}
                  onClick={() => onDelete(m.id)}
                  className="text-[var(--tha-muted)] hover:text-[var(--tha-red)]"
                  aria-label="Verwijder"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * RequestToInvoiceModal — quick modal that lets admin add invoice line items,
 * shipping cost, VAT rate and payment terms. Recipient fields are auto-pulled
 * from the B2B request (company/contact/email/kvk/btw/address/city).
 */
const RequestToInvoiceModal = ({ req, authHeaders, onClose, onSuccess }) => {
  const initialItems = () => {
    // Pre-fill line items from the request's interested_products (if any)
    const interests = req.interested_products || [];
    if (interests.length > 0) {
      return interests.map((p) => ({ description: p, quantity: 1, unit_price_excl: 0 }));
    }
    return [{ description: "", quantity: 1, unit_price_excl: 0 }];
  };
  const [items, setItems] = useState(initialItems);
  const [paymentTermsDays, setPaymentTermsDays] = useState(14);
  const [vatRate, setVatRate] = useState(21);
  const [shippingCost, setShippingCost] = useState(0);
  const [reference, setReference] = useState(req.company_name || "");
  const [notes, setNotes] = useState(req.message || "");
  const [loading, setLoading] = useState(false);

  const updateItem = (idx, patch) => {
    setItems((its) => its.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((its) => [...its, { description: "", quantity: 1, unit_price_excl: 0 }]);
  const removeItem = (idx) => setItems((its) => (its.length > 1 ? its.filter((_, i) => i !== idx) : its));

  const subtotal = items.reduce(
    (s, it) => s + Number(it.quantity || 0) * Number(it.unit_price_excl || 0),
    0
  );
  const taxable = subtotal + Number(shippingCost || 0);
  const vat = (taxable * Number(vatRate || 0)) / 100;
  const total = taxable + vat;

  const submit = async (e) => {
    e.preventDefault();
    const clean = items
      .map((it) => ({
        description: (it.description || "").trim(),
        quantity: Number(it.quantity),
        unit_price_excl: Number(it.unit_price_excl),
      }))
      .filter((it) => it.description && it.quantity > 0);
    if (clean.length === 0) {
      toast.error("Voeg minstens 1 factuurregel toe.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/requests/b2b/${req.id}/create-invoice`,
        {
          items: clean,
          payment_terms_days: Number(paymentTermsDays),
          vat_rate: Number(vatRate),
          shipping_cost: Number(shippingCost),
          reference: reference || undefined,
          notes: notes || undefined,
        },
        { headers: authHeaders }
      );
      onSuccess?.(data.invoice);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Factuur aanmaken mislukt.";
      toast.error(typeof msg === "string" ? msg : "Factuur aanmaken mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="request-to-invoice-modal"
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-[#0a0a0a] border border-[var(--tha-border)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--tha-border)]">
          <div>
            <div className="text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              AANVRAAG → FACTUUR
            </div>
            <div className="font-display text-3xl mt-1 leading-none">
              CONCEPT VOOR <span className="neon-red-text">{req.company_name?.toUpperCase()}</span>
            </div>
          </div>
          <button
            data-testid="invoice-modal-close"
            onClick={onClose}
            className="p-2 hover:bg-white/5 text-white"
            aria-label="Sluiten"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 sm:p-8">
          {/* Recipient summary (read-only, pre-filled from request) */}
          <div className="p-4 border border-[var(--tha-border)] bg-[#050505] mb-6">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
              GEADRESSEERDE (UIT AANVRAAG)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="text-white">{req.company_name}</div>
              <div className="text-white">{req.contact_name}</div>
              <div className="text-[var(--tha-muted)]">{req.email}</div>
              <div className="text-[var(--tha-muted)]">
                {req.kvk ? `KvK ${req.kvk}` : "—"} · {req.vat_number || "—"}
              </div>
              <div className="text-[var(--tha-muted)] sm:col-span-2">
                {req.address || "—"} {req.city || ""}
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
                FACTUURREGELS
              </div>
              <button
                type="button"
                data-testid="invoice-add-item"
                onClick={addItem}
                className="text-xs font-mono-x tracking-widest inline-flex items-center gap-1 text-[var(--tha-red)] hover:text-white"
              >
                <Plus size={12} /> REGEL
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input
                    data-testid={`invoice-item-desc-${idx}`}
                    type="text"
                    required
                    value={it.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                    placeholder="Omschrijving"
                    className="col-span-6 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <input
                    data-testid={`invoice-item-qty-${idx}`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    placeholder="Aantal"
                    className="col-span-2 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <input
                    data-testid={`invoice-item-price-${idx}`}
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={it.unit_price_excl}
                    onChange={(e) => updateItem(idx, { unit_price_excl: e.target.value })}
                    placeholder="€ excl."
                    className="col-span-3 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <button
                    type="button"
                    data-testid={`invoice-remove-item-${idx}`}
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    className="col-span-1 flex items-center justify-center text-[var(--tha-muted)] hover:text-[var(--tha-red)] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Verwijder regel"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                BETAALTERMIJN (DGN)
              </span>
              <input
                data-testid="invoice-payment-days"
                type="number"
                min="1"
                max="180"
                value={paymentTermsDays}
                onChange={(e) => setPaymentTermsDays(e.target.value)}
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                BTW %
              </span>
              <input
                data-testid="invoice-vat-rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                VERZENDING (€)
              </span>
              <input
                data-testid="invoice-shipping"
                type="number"
                min="0"
                step="0.01"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                REFERENTIE
              </span>
              <input
                data-testid="invoice-reference"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
              />
            </label>
          </div>

          {/* Totals preview */}
          <div className="p-4 border border-[var(--tha-border)] bg-[#050505] mb-6">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
              LIVE PREVIEW
            </div>
            <div className="space-y-1 text-sm">
              <TotalLine label="Subtotaal excl. BTW" value={subtotal} />
              {Number(shippingCost) > 0 && <TotalLine label="Verzendkosten excl." value={Number(shippingCost)} />}
              <TotalLine label={`BTW (${vatRate}%)`} value={vat} />
              <div className="pt-2 border-t border-[var(--tha-border)] flex justify-between font-display text-xl">
                <span>TOTAAL INCL.</span>
                <span data-testid="invoice-total-incl" className="neon-red-text">
                  € {total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <label className="flex flex-col gap-1 mb-6">
            <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              NOTITIES OP FACTUUR
            </span>
            <textarea
              data-testid="invoice-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white resize-none focus:border-white outline-none"
            />
          </label>

          <button
            data-testid="invoice-create-submit"
            type="submit"
            disabled={loading}
            className="w-full py-4 font-display tracking-wider btn-red inline-flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {loading ? "AANMAKEN..." : "MAAK CONCEPT-FACTUUR AAN"}
          </button>
          <p className="mt-3 text-center text-xs text-[var(--tha-muted)] font-mono-x">
            De aanvraag wordt automatisch op &apos;OFFERTE VERZONDEN&apos; gezet.
          </p>
        </form>
      </div>
    </div>
  );
};

const TotalLine = ({ label, value }) => (
  <div className="flex justify-between text-white">
    <span className="text-[var(--tha-muted)]">{label}</span>
    <span>€ {value.toFixed(2)}</span>
  </div>
);

/**
 * AnalyticsFunnel — compact conversion funnel + KPI card. Shows last 90 days:
 * total → quoted → won (with rate %), plus average B2B deal value.
 */
const AnalyticsFunnel = ({ analytics }) => {
  const b2b = analytics.b2b || {};
  const c = analytics.consumer || {};
  return (
    <div data-testid="requests-analytics" className="mb-8 border border-[var(--tha-border)] bg-[#0a0a0a]">
      <div className="px-5 py-3 border-b border-[var(--tha-border)] flex items-center justify-between">
        <div className="text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
          CONVERSIE · LAATSTE {analytics.period_days} DAGEN
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--tha-border)]">
        {/* B2B */}
        <FunnelBlock
          title="ZAKELIJK"
          totalLabel="AANVRAGEN"
          total={b2b.total || 0}
          quoted={(b2b.quoted || 0) + (b2b.won || 0)}
          won={b2b.won || 0}
          quoteRate={b2b.quote_rate || 0}
          winRate={b2b.win_rate || 0}
          avg={b2b.avg_deal_value}
          pipeline={b2b.pipeline_value}
          testId="funnel-b2b"
        />
        {/* Consumer */}
        <FunnelBlock
          title="CONSUMENTEN"
          totalLabel="AANVRAGEN"
          total={c.total || 0}
          quoted={(c.quoted || 0) + (c.won || 0)}
          won={c.won || 0}
          quoteRate={c.quote_rate || 0}
          winRate={c.win_rate || 0}
          testId="funnel-consumer"
        />
      </div>
    </div>
  );
};

const FunnelBlock = ({ title, totalLabel, total, quoted, won, quoteRate, winRate, avg, pipeline, testId }) => (
  <div data-testid={testId} className="bg-[#0a0a0a] p-5">
    <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)] mb-4">{title}</div>
    <div className="grid grid-cols-3 gap-3">
      <FunnelStep label={totalLabel} value={total} accent="#6b7280" />
      <FunnelStep label="OFFERTE" value={quoted} accent="#3b82f6" rate={quoteRate} />
      <FunnelStep label="GEWONNEN" value={won} accent="#10b981" rate={winRate} />
    </div>
    {typeof avg === "number" && (
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="border border-[var(--tha-border)] p-3">
          <div className="text-[9px] font-mono-x tracking-widest text-[var(--tha-muted)]">
            GEM. DEAL
          </div>
          <div className="font-display text-2xl mt-1 text-white">
            € {avg.toFixed(2)}
          </div>
        </div>
        <div className="border border-[var(--tha-border)] p-3">
          <div className="text-[9px] font-mono-x tracking-widest text-[var(--tha-muted)]">
            PIPELINE
          </div>
          <div className="font-display text-2xl mt-1 neon-red-text">
            € {(pipeline || 0).toFixed(2)}
          </div>
        </div>
      </div>
    )}
  </div>
);

const FunnelStep = ({ label, value, accent, rate }) => (
  <div className="border border-[var(--tha-border)] p-3">
    <div className="text-[9px] font-mono-x tracking-widest text-[var(--tha-muted)]">{label}</div>
    <div className="font-display text-3xl mt-1" style={{ color: accent }}>
      {value}
    </div>
    {typeof rate === "number" && (
      <div className="text-[9px] font-mono-x text-[var(--tha-muted)] mt-1">
        {rate.toFixed(1)}%
      </div>
    )}
  </div>
);

/**
 * ConsumerToOrderModal — admin-side workflow to convert a consumer request into
 * an actual order document. Bypasses Mollie (payment_status='admin_manual').
 */
const ConsumerToOrderModal = ({ req, authHeaders, onClose, onSuccess }) => {
  const initialItems = () => {
    const its = req.items || [];
    if (its.length > 0) {
      return its.map((it) => ({
        product_name: it.product_name || "",
        variant_label: it.variant_label || "",
        quantity: it.quantity || 1,
        unit_price: 0,
      }));
    }
    return [{ product_name: "", variant_label: "", quantity: 1, unit_price: 0 }];
  };
  const [items, setItems] = useState(initialItems);
  const [pickup, setPickup] = useState(false);
  const [shippingCost, setShippingCost] = useState(5.95);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState(req.message || "");
  const [loading, setLoading] = useState(false);

  const updateItem = (idx, patch) =>
    setItems((its) => its.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((its) => [...its, { product_name: "", variant_label: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) =>
    setItems((its) => (its.length > 1 ? its.filter((_, i) => i !== idx) : its));

  const subtotal = items.reduce(
    (s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0),
    0
  );
  const shipping = pickup ? 0 : Number(shippingCost || 0);
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    const clean = items
      .map((it) => ({
        product_name: (it.product_name || "").trim(),
        variant_label: (it.variant_label || "").trim() || undefined,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price),
      }))
      .filter((it) => it.product_name && it.quantity > 0);
    if (clean.length === 0) {
      toast.error("Voeg minstens 1 regel toe.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/requests/consumer/${req.id}/create-order`,
        {
          items: clean,
          shipping_cost: Number(shippingCost),
          pickup,
          address: address || undefined,
          notes: notes || undefined,
        },
        { headers: authHeaders }
      );
      onSuccess?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bestelling aanmaken mislukt.";
      toast.error(typeof msg === "string" ? msg : "Bestelling aanmaken mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="consumer-to-order-modal"
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-[#0a0a0a] border border-[var(--tha-border)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--tha-border)]">
          <div>
            <div className="text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              AANVRAAG → BESTELLING
            </div>
            <div className="font-display text-3xl mt-1 leading-none">
              MAAK BESTELLING VOOR <span className="neon-red-text">{req.name?.toUpperCase()}</span>
            </div>
          </div>
          <button
            data-testid="order-modal-close"
            onClick={onClose}
            className="p-2 hover:bg-white/5 text-white"
            aria-label="Sluiten"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 sm:p-8">
          {/* Customer summary */}
          <div className="p-4 border border-[var(--tha-border)] bg-[#050505] mb-6">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
              KLANT (UIT AANVRAAG)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="text-white">{req.name}</div>
              <div className="text-[var(--tha-muted)]">{req.email}</div>
              <div className="text-[var(--tha-muted)]">{req.phone}</div>
              <div className="text-[var(--tha-muted)]">
                {req.postal_code} {req.city}
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
                ORDERREGELS
              </div>
              <button
                type="button"
                data-testid="order-add-item"
                onClick={addItem}
                className="text-xs font-mono-x tracking-widest inline-flex items-center gap-1 text-[var(--tha-red)] hover:text-white"
              >
                <Plus size={12} /> REGEL
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input
                    data-testid={`order-item-name-${idx}`}
                    type="text"
                    required
                    value={it.product_name}
                    onChange={(e) => updateItem(idx, { product_name: e.target.value })}
                    placeholder="Product"
                    className="col-span-4 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <input
                    data-testid={`order-item-variant-${idx}`}
                    type="text"
                    value={it.variant_label}
                    onChange={(e) => updateItem(idx, { variant_label: e.target.value })}
                    placeholder="Variant (12-pack…)"
                    className="col-span-3 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <input
                    data-testid={`order-item-qty-${idx}`}
                    type="number"
                    min="1"
                    required
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    placeholder="Aantal"
                    className="col-span-2 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <input
                    data-testid={`order-item-price-${idx}`}
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={it.unit_price}
                    onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                    placeholder="€ per stuk"
                    className="col-span-2 px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
                  />
                  <button
                    type="button"
                    data-testid={`order-remove-item-${idx}`}
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    className="col-span-1 flex items-center justify-center text-[var(--tha-muted)] hover:text-[var(--tha-red)] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Verwijder"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Fulfilment */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <label className="flex items-center gap-3 p-3 border border-[var(--tha-border)] bg-[#050505] cursor-pointer">
              <input
                data-testid="order-pickup"
                type="checkbox"
                checked={pickup}
                onChange={(e) => setPickup(e.target.checked)}
                className="w-4 h-4 accent-[var(--tha-red)]"
              />
              <span className="text-sm text-white">OPHALEN IN ASSEN (gratis)</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                VERZENDKOSTEN (€)
              </span>
              <input
                data-testid="order-shipping"
                type="number"
                min="0"
                step="0.01"
                value={shippingCost}
                disabled={pickup}
                onChange={(e) => setShippingCost(e.target.value)}
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none disabled:opacity-40"
              />
            </label>
          </div>

          {!pickup && (
            <label className="flex flex-col gap-1 mb-6">
              <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                VERZENDADRES (STRAAT + HUISNUMMER)
              </span>
              <input
                data-testid="order-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Bijv. Voorbeeldstraat 12"
                className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white focus:border-white outline-none"
              />
            </label>
          )}

          {/* Totals */}
          <div className="p-4 border border-[var(--tha-border)] bg-[#050505] mb-6">
            <div className="space-y-1 text-sm">
              <TotalLine label="Subtotaal" value={subtotal} />
              <TotalLine label={pickup ? "Ophalen in Assen" : "Verzendkosten"} value={shipping} />
              <div className="pt-2 border-t border-[var(--tha-border)] flex justify-between font-display text-xl">
                <span>TOTAAL</span>
                <span data-testid="order-total" className="neon-red-text">
                  € {total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-6">
            <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              INTERNE NOTITIES
            </span>
            <textarea
              data-testid="order-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="px-3 py-2 bg-[#050505] border border-[var(--tha-border)] text-sm text-white resize-none focus:border-white outline-none"
            />
          </label>

          <button
            data-testid="order-create-submit"
            type="submit"
            disabled={loading}
            className="w-full py-4 font-display tracking-wider btn-red inline-flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
            {loading ? "AANMAKEN..." : "MAAK HANDMATIGE BESTELLING"}
          </button>
          <p className="mt-3 text-center text-xs text-[var(--tha-muted)] font-mono-x">
            De aanvraag wordt op &apos;GEWONNEN&apos; gezet · Skipt Mollie · Verschijnt bij Bestellingen
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminRequests;
